import { Box, Tabs, type TabsProps, Text, Tooltip } from '@mantine/core';
import {
  type FieldValues,
  type UseControllerProps,
  useController,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InfoIcon } from '../../../icons';

export type TabsFieldProps<T extends FieldValues> = UseControllerProps<T> &
  Omit<TabsProps, 'value' | 'defaultValue'> & {
    hint?: string;
    tabs: string[];
  };

/**
 * TabsField component
 *
 * @example
 * ```tsx
 * <TabsField name="field.name" >
 *   <Tabs.List>
 *     <Tabs.Tab value='tab1'>label 1</Tabs.Tab>
 *     <Tabs.Tab value='tab2'>label 2</Tabs.Tab>
 *     <Tabs.Tab value='tab3'>label 3</Tabs.Tab>
 *   </Tabs.List>
 * </TabsField>
 * ```
 *
 * @public
 * @param name - field name
 * @param control - form control
 * @param defaultValue - default value
 * @param rules - validation rules
 * @param shouldUnregister - unregister field when unmount
 * @param onChange - onChange event
 * @param tabs - tabs to display
 * @param hint - hint to display
 * @param props - other props
 */
export function TabsField<T extends FieldValues>({
  name,
  control,
  defaultValue,
  rules,
  shouldUnregister,
  tabs,
  hint,
  children,
  ...props
}: TabsFieldProps<T>) {
  const { t } = useTranslation();
  const {
    field: { value, onChange: fieldOnChange, ref, ...field },
    fieldState,
  } = useController<T>({
    name,
    control,
    defaultValue,
    rules,
    shouldUnregister,
  });

  return (
    <>
      <Tabs
        value={value}
        onTabChange={tabValue => fieldOnChange(tabValue)}
        {...field}
        {...props}
      >
        <Box sx={{ display: 'flex', gap: 8 }}>
          <Tabs.List sx={theme => ({ marginBottom: theme.other.spacing(2) })}>
            {tabs
              .filter(t => !(typeof t === 'string' && t.trim() === ''))
              .map((tab, index) => (
                <Tabs.Tab
                  key={tab}
                  value={tab}
                  ref={index === 0 ? ref : undefined}
                >
                  {tab}
                </Tabs.Tab>
              ))}
          </Tabs.List>
          {hint && (
            <Tooltip multiline withinPortal sx={{ maxWidth: 600 }} label={hint}>
              <Box>
                <InfoIcon size={20} />
              </Box>
            </Tooltip>
          )}
          {children}
        </Box>
      </Tabs>
      {fieldState.error?.message && (
        <Text color="red" mt="xs">
          {t(fieldState.error.message)}
        </Text>
      )}
    </>
  );
}
